import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react';
import clsx from 'clsx';
import './Link.css';

export type LinkTone = 'default' | 'muted';

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> {
  href: string;
  /**
   * Force external behavior (target=_blank + rel=noopener noreferrer).
   * Default: auto-detected from absolute http(s) URLs.
   */
  external?: boolean;
  tone?: LinkTone;
  /** Underline style. `always` = persistent, `hover` = appears on hover/focus. */
  underline?: 'always' | 'hover';
  /**
   * Optional — react-i18next's `<Trans>` component injects children via
   * `cloneElement` at runtime, so consumers using interpolation can pass
   * `<Link href="..." />` without explicit children.
   */
  children?: ReactNode;
  ref?: Ref<HTMLAnchorElement>;
}

/**
 * Link — text-link primitive. Inline-friendly, uses brand-green hover.
 *
 * For internal SPA navigation prefer your router's own Link component
 * (e.g. react-router); this primitive targets external destinations such
 * as legal pages, support, and marketing.
 */
export function Link({
  href,
  external,
  tone = 'default',
  underline = 'always',
  className,
  children,
  target,
  rel,
  ref,
  ...rest
}: LinkProps) {
  const isExternal = external ?? /^https?:\/\//i.test(href);
  const targetAttr = isExternal ? (target ?? '_blank') : target;
  const relAttr = isExternal ? (rel ?? 'noopener noreferrer') : rel;
  return (
    <a
      ref={ref}
      href={href}
      target={targetAttr}
      rel={relAttr}
      className={clsx('fluent-link', className)}
      data-tone={tone === 'default' ? undefined : tone}
      data-underline={underline}
      {...rest}
    >
      {children}
    </a>
  );
}
